import { css } from '@emotion/react';
import {
  Box,
  Flex,
  Icon,
  Stack,
  Text,
  Title,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { faArrowRight } from '@inflearn/pro-regular-svg-icons';
import { useTranslation } from 'next-i18next';

import StartInstructorButton from './StartInstructorButton';
import { JOIN_INSTRUCTOR_SPOTS } from './constants/gtmEventParams';

const ProcessSection = () => {
  const { t } = useTranslation();

  const STEPS = [
    {
      number: 1,
      title: t('open.knowledge.process.step1.title'),
      description: t('open.knowledge.process.step1.description'),
      active: true,
    },
    {
      number: 2,
      title: t('open.knowledge.process.step2.title'),
      description: t('open.knowledge.process.step2.description'),
      active: false,
    },
    {
      number: 3,
      title: t('open.knowledge.process.step3.title'),
      description: t('open.knowledge.process.step3.description'),
      active: false,
    },
    {
      number: 4,
      title: t('open.knowledge.process.step4.title'),
      description: t('open.knowledge.process.step4.description'),
      active: false,
    },
  ];

  return (
    <Box component="section" css={styleSection}>
      <Flex
        direction="column"
        align="center"
        styles={{
          root: styleContainer,
        }}
      >
        <Stack
          gap={40}
          align="center"
          styles={{
            root: styleContents,
          }}
        >
          <Title
            order={2}
            ta="center"
            c="white"
            lh={1.3}
            styles={{
              root: styleSectionTitle,
            }}
          >
            {t('open.knowledge.process.title')}
          </Title>
          <Stack
            gap={12}
            align="center"
            styles={{
              root: styleStepGroup,
            }}
          >
            {STEPS.map((step) => (
              <Box key={step.number} css={styleStepCard}>
                <Flex align="center" gap={16}>
                  <Flex
                    align="center"
                    justify="center"
                    styles={{
                      root: styleStepNumber(step.active),
                    }}
                  >
                    <Text
                      c={step.active ? 'white' : 'dark.9'}
                      fw={700}
                      styles={{
                        root: styleStepNumberText,
                      }}
                    >
                      {step.number}
                    </Text>
                  </Flex>
                  <Box>
                    <Text
                      c="white"
                      fw={700}
                      lh={1.5}
                      styles={{
                        root: styleStepTitle,
                      }}
                    >
                      {step.title}
                    </Text>
                    <Text
                      c="dark.0"
                      lh={1.5}
                      styles={{
                        root: styleStepDesc,
                      }}
                    >
                      {step.description}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            ))}
          </Stack>
          <StartInstructorButton
            spot={JOIN_INSTRUCTOR_SPOTS.SHARE_METHOD}
            variant="filled"
            color="infgreen"
            size="lg"
            radius="xl"
            rightSection={<Icon icon={faArrowRight} size="lg" color="white" />}
          >
            {t('open.knowledge.process.cta')}
          </StartInstructorButton>
        </Stack>
      </Flex>
    </Box>
  );
};

export default ProcessSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#000000',
  padding: '100px 0',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  maxWidth: 1200,
  width: '100%',
});

const styleSectionTitle = css({
  fontSize: 34,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 24,
  },
});

const styleStepGroup = css({
  width: '100%',
  maxWidth: 640,
});

const styleStepCard = css({
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  borderRadius: 16,
  padding: '24px 32px',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '16px 16px',
  },
});

const styleStepNumber = (active: boolean) =>
  css({
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: active ? '#00a760' : '#ffffff',
    flexShrink: 0,
  });

const styleStepNumberText = css({
  fontSize: 16,
  fontWeight: 700,
});

const styleStepTitle = css({
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 16,
  },
});

const styleStepDesc = css({
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 14,
  },
});
